'use client';

import { useState, useCallback, useEffect } from 'react';
import { ArtifactContent, ArtifactVersion } from '@/components/chat/ContentArtifact';

interface UseContentArtifactsReturn {
  artifacts: ArtifactContent[];
  activeArtifact: ArtifactContent | null;
  showArtifacts: boolean;
  sidebarWidth: number;
  addArtifact: (artifact: Omit<ArtifactContent, 'id' | 'timestamp' | 'version' | 'history' | 'lastUpdated'>) => ArtifactContent;
  updateArtifact: (id: string, content: string, editReason?: string) => void;
  updateArtifactWithVersion: (id: string, newContent: string, newTitle: string, editReason?: string) => void;
  removeArtifact: (id: string) => void;
  setActiveArtifact: (artifact: ArtifactContent | null) => void;
  setArtifacts: React.Dispatch<React.SetStateAction<ArtifactContent[]>>;
  clearArtifacts: () => void;
  toggleArtifactsPanel: () => void;
  setSidebarWidth: (width: number) => void;
  findSimilarArtifact: (content: string, contentType?: string) => ArtifactContent | null;
}

const SIDEBAR_WIDTH_KEY = 'artifacts-sidebar-width';
const DEFAULT_SIDEBAR_WIDTH = 360; // More reasonable default for better chat space
const MIN_SIDEBAR_WIDTH = 320;
const MAX_SIDEBAR_WIDTH = 600; // Reduced max to prevent excessive compression

export function useContentArtifacts(): UseContentArtifactsReturn {
  const [artifacts, setArtifacts] = useState<ArtifactContent[]>([]);
  const [activeArtifact, setActiveArtifact] = useState<ArtifactContent | null>(null);
  const [showArtifacts, setShowArtifacts] = useState(false);
  const [sidebarWidth, setSidebarWidthState] = useState(DEFAULT_SIDEBAR_WIDTH);
  const [isPreloading, setIsPreloading] = useState(false);

  // Load saved sidebar width from localStorage on mount
  useEffect(() => {
    const savedWidth = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    if (savedWidth) {
      const width = parseInt(savedWidth, 10);
      if (width >= MIN_SIDEBAR_WIDTH && width <= MAX_SIDEBAR_WIDTH) {
        setSidebarWidthState(width);
      }
    }
  }, []);

  const setSidebarWidth = useCallback((width: number) => {
    const clampedWidth = Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, width));
    setSidebarWidthState(clampedWidth);
    localStorage.setItem(SIDEBAR_WIDTH_KEY, clampedWidth.toString());
  }, []);

  const addArtifact = useCallback((artifact: Omit<ArtifactContent, 'id' | 'timestamp' | 'version' | 'history' | 'lastUpdated'>): ArtifactContent => {
    const now = new Date();
    const newArtifact: ArtifactContent = {
      ...artifact,
      id: `artifact-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: now,
      version: 1,
      history: [],
      lastUpdated: now,
    };

    setArtifacts(prev => [...prev, newArtifact]);
    setActiveArtifact(newArtifact);
    setShowArtifacts(true);
    
    return newArtifact;
  }, []);

  const updateArtifact = useCallback((id: string, content: string, editReason?: string) => {
    setArtifacts(prev => prev.map(artifact => 
      artifact.id === id 
        ? { ...artifact, content, isEdited: true, lastUpdated: new Date() }
        : artifact
    ));
    
    // Update active artifact if it's the one being updated
    setActiveArtifact(prev => 
      prev && prev.id === id 
        ? { ...prev, content, isEdited: true, lastUpdated: new Date() }
        : prev
    );
  }, []);

  const updateArtifactWithVersion = useCallback((id: string, newContent: string, newTitle: string, editReason?: string) => {
    setArtifacts(prev => prev.map(artifact => {
      if (artifact.id !== id) return artifact;
      
      // Create version from current state
      const previousVersion: ArtifactVersion = {
        content: artifact.content,
        title: artifact.title,
        timestamp: artifact.lastUpdated,
        editReason: 'Previous version',
        agentType: artifact.agentType
      };
      
      // Update artifact with new version
      return {
        ...artifact,
        content: newContent,
        title: newTitle,
        version: artifact.version + 1,
        history: [...artifact.history, previousVersion],
        lastUpdated: new Date(),
        isEdited: true
      };
    }));
    
    // Update active artifact if it's the one being updated
    setActiveArtifact(prev => {
      if (!prev || prev.id !== id) return prev;
      
      const previousVersion: ArtifactVersion = {
        content: prev.content,
        title: prev.title,
        timestamp: prev.lastUpdated,
        editReason: 'Previous version',
        agentType: prev.agentType
      };
      
      return {
        ...prev,
        content: newContent,
        title: newTitle,
        version: prev.version + 1,
        history: [...prev.history, previousVersion],
        lastUpdated: new Date(),
        isEdited: true
      };
    });
  }, []);

  const findSimilarArtifact = useCallback((content: string, contentType?: string): ArtifactContent | null => {
    const { calculateContentSimilarity } = require('@/lib/artifactDetection');
    
    let bestMatch: ArtifactContent | null = null;
    let bestSimilarity = 0;
    
    // Sort artifacts by recency, giving priority to more recent ones
    const sortedArtifacts = [...artifacts].sort((a, b) => {
      const aTime = a.lastUpdated?.getTime() || a.timestamp?.getTime() || 0;
      const bTime = b.lastUpdated?.getTime() || b.timestamp?.getTime() || 0;
      return bTime - aTime;
    });
    
    for (const artifact of sortedArtifacts) {
      // Check content type match if specified
      if (contentType && artifact.type !== contentType) continue;
      
      const similarity = calculateContentSimilarity(content, artifact.content);
      
      // Consider it a match if similarity is above 0.3 (30%) - more lenient
      // Give slight bonus to more recent artifacts
      const recencyBonus = sortedArtifacts.indexOf(artifact) < 3 ? 0.05 : 0;
      const adjustedSimilarity = similarity + recencyBonus;
      
      if (adjustedSimilarity > 0.3 && adjustedSimilarity > bestSimilarity) {
        bestMatch = artifact;
        bestSimilarity = adjustedSimilarity;
      }
    }
    
    return bestMatch;
  }, [artifacts]);

  const removeArtifact = useCallback((id: string) => {
    setArtifacts(prev => prev.filter(artifact => artifact.id !== id));
    
    // Clear active artifact if it's the one being removed
    setActiveArtifact(prev => prev && prev.id === id ? null : prev);
    
    // Hide panel if no artifacts left
    setArtifacts(prev => {
      if (prev.length === 0) {
        setShowArtifacts(false);
      }
      return prev;
    });
  }, []);

  const clearArtifacts = useCallback(() => {
    setArtifacts([]);
    setActiveArtifact(null);
    setShowArtifacts(false);
  }, []);

  const toggleArtifactsPanel = useCallback(() => {
    if (!showArtifacts) {
      setIsPreloading(true);
      // Preload artifacts panel with immediate feedback
      setTimeout(() => {
        setShowArtifacts(true);
        setIsPreloading(false);
      }, 50);
    } else {
      setShowArtifacts(false);
    }
  }, [showArtifacts]);

  // Listen for global toggle event from nav sidebar
  useEffect(() => {
    const handleToggleEvent = () => {
      toggleArtifactsPanel();
    };

    window.addEventListener('chat:artifacts:toggle', handleToggleEvent);
    return () => window.removeEventListener('chat:artifacts:toggle', handleToggleEvent);
  }, [toggleArtifactsPanel]);

  return {
    artifacts,
    activeArtifact,
    showArtifacts,
    sidebarWidth,
    addArtifact,
    updateArtifact,
    updateArtifactWithVersion,
    removeArtifact,
    setActiveArtifact,
    setArtifacts,
    clearArtifacts,
    toggleArtifactsPanel,
    setSidebarWidth,
    findSimilarArtifact,
  };
}